/** Map related utility functions */
import { Control, ZoomSlider } from 'ol/control';
import { Feature, Map } from 'ol';
import { Polygon } from 'ol/geom';
import { Style, Circle, Fill, Stroke, Text as OlText, Icon } from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import { Circle as CircleStyle, RegularShape, Text } from 'ol/style';
import { get as getProjection } from 'ol/proj';
import { FeatureLike } from 'ol/Feature';
import { FieldType, LayerDefinition } from '@packages/database/types';
import { useCallback } from 'react';
import LayerGroup from 'ol/layer/Group';

export const hexToRgba = (hex: string, alpha: number) => {
  // Return explicit rgba if alpha is not 1, otherwise return hex
  if (alpha === 1) return hex;

  let r = 0,
    g = 0,
    b = 0;
  // Handle 3-digit hex
  if (hex.length === 4) {
    r = parseInt('0x' + hex[1] + hex[1]);
    g = parseInt('0x' + hex[2] + hex[2]);
    b = parseInt('0x' + hex[3] + hex[3]);
  }
  // Handle 6-digit hex
  else if (hex.length === 7) {
    r = parseInt('0x' + hex[1] + hex[2]);
    g = parseInt('0x' + hex[3] + hex[4]);
    b = parseInt('0x' + hex[5] + hex[6]);
  }
  return `rgba(${r},${g},${b},${alpha})`;
};

// helpers/fieldUtils.ts — shared between FeatureCard, FeatureInfoPopup, etc.
export function formatFieldValue(fieldType: FieldType, value: unknown): string {
  if (value == null) return '—';
  switch (fieldType) {
    case FieldType.DATE:
      return new Date(String(value)).toLocaleDateString();
    case FieldType.BOOLEAN:
      return value ? 'Yes' : 'No';
    case FieldType.NUMBER:
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    default:
      return String(value);
  }
}

// resolveLabel used by VirtualizedList getLabel prop
// export function resolveLabel(
//   feature: ProjectAreaFeature,
//   layerDef?: ResolvedLayer,
// ): string {
//   const labelField = layerDef?.styles.labelField ?? 'name';
//   return String(feature.properties[labelField] ?? feature.id);
// }

function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max - 3) + '…' : str;
}

export const createStyleFunction = (layerStyles: LayerDefinition['styles']) => {
  const config = layerStyles;

  const mainColor = config.color || '#3b82f6';
  const fillColor = config.fillColor || mainColor;
  const strokeColor = config.strokeColor || '#ffffff';
  const labelField = config?.label?.field || '';

  const opacity = config.opacity ?? 1.0;

  const pointFill = new Fill({
    color: hexToRgba(mainColor, opacity),
  });
  const pointStroke = new Stroke({
    color: strokeColor,
    width: 2,
  });

  const polygonFill = new Fill({
    color: hexToRgba(fillColor, opacity),
  });

  const lineStroke = new Stroke({
    color: config.strokeColor || mainColor, // Lines use the main color if no specific stroke is set
    width: config.lineWidth || 2,
    lineDash: config.lineDash,
  });

  return (feature: FeatureLike, resolution: number): Style | Style[] => {
    const geomType = feature.getGeometry()?.getType();
    const style = new Style({});

    if (geomType === 'Point' || geomType === 'MultiPoint') {
      const size = config.pointSize || 6;

      if (config.pointType === 'icon' && config.iconUrl) {
        // Icon Logic
        style.setImage(
          new Icon({
            src: config.iconUrl,
            scale: size / 24, // Assumes base icon is ~24px
            color: config.color, // Tint
            rotation: (config.iconRotation || 0) * (Math.PI / 180),
            crossOrigin: 'anonymous',
          }),
        );
      } else if (
        ['square', 'triangle', 'star'].includes(config.pointType || '')
      ) {
        // Shape Logic
        let points = 4;
        let radius = size;
        let radius2 = undefined;
        let angle = 0;

        switch (config.pointType) {
          case 'square':
            points = 4;
            angle = Math.PI / 4;
            break;
          case 'triangle':
            points = 3;
            angle = 0;
            break;
          case 'star':
            points = 5;
            radius2 = size * 0.4;
            angle = 0;
            break;
        }

        style.setImage(
          new RegularShape({
            fill: pointFill,
            stroke: pointStroke,
            points: points,
            radius: radius,
            radius2: radius2,
            angle: angle,
          }),
        );
      } else {
        // Default Circle
        style.setImage(
          new CircleStyle({
            radius: size,
            fill: pointFill,
            stroke: pointStroke,
          }),
        );
      }
    }

    // --- Line Styling ---
    if (geomType === 'LineString' || geomType === 'MultiLineString') {
      style.setStroke(lineStroke);
    }

    // --- Polygon Styling ---
    if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
      style.setFill(polygonFill);
      style.setStroke(lineStroke); // Polygons use the "Line" stroke for their border
    }

    // --- Labeling ---
    if (config.showLabel && labelField) {
      const labelText = feature.get(labelField) || '';
      if (labelText) {
        style.setText(
          new Text({
            text: truncate(
              String(labelText ?? ''),
              config.label.maxLength || 40,
            ),
            font: config.label.font,
            fill: new Fill({ color: config.label.color || '#000000' }),
            stroke: new Stroke({
              color: config.label.background || '#ffffff',
              width: 3,
            }),
            offsetY: config.label.offsetY || -12,
          }),
        );
      }
    }

    return style;
  };
};

export const createHighlightStyle = (): ((feature: Feature) => Style[]) => {
  const highlightStyle = new Style({
    image: new Circle({
      radius: 10,
      stroke: new Stroke({ color: '#facc15', width: 4 }),
    }),
    stroke: new Stroke({ color: '#facc15', width: 8 }),
    fill: new Fill({ color: 'rgba(250, 204, 21, 0.3)' }),
    zIndex: 99,
  });

  return (feature: Feature) => {
    const originalStyle = feature.getStyle() as Style;

    if (originalStyle?.getImage()) {
      const originalRadius = (originalStyle.getImage() as Circle).getRadius();
      (highlightStyle.getImage() as Circle).setRadius(originalRadius + 6);
    }

    return [originalStyle, highlightStyle];
  };
};

// export const createGridLayer = (source: any) => {
//   const proj = getProjection('EPSG:3857');
//   const extent = proj!.getExtent(); // project's specific extent

//   const gridSize = 500000; // 100km

//   for (let x = extent[0]; x < extent[2]; x += gridSize) {
//     for (let y = extent[1]; y < extent[3]; y += gridSize) {
//       const cellPolygon = new Polygon([
//         [
//           [x, y],
//           [x + gridSize, y],
//           [x + gridSize, y + gridSize],
//           [x, y + gridSize],
//           [x, y],
//         ],
//       ]);
//       const cellFeature = new Feature({ geometry: cellPolygon });
//       source.addFeature(cellFeature);
//     }
//   }

//   const style = new Style({
//     stroke: new Stroke({
//       color: 'rgba(0, 0, 0, 0.2)',
//       width: 1,
//     }),
//     fill: new Fill({
//       color: 'rgba(0, 0, 0, 0.001)',
//     }),
//   });

//   return new VectorLayer({
//     source: source,
//     style: style,
//     visible: false,
//     zIndex: 0,
//   });
// }

// Create custom google logo control
export const createGoogleLogoControl = () => {
  const element = document.createElement('div');

  // Added 'ol-google-logo' for targeting and 'hidden' to start invisible
  element.className =
    'ol-google-logo hidden pointer-events-none absolute bottom-1 left-1 z-100';

  const image = document.createElement('img');
  image.src =
    'https://maps.gstatic.com/mapfiles/api-3/images/google_white5.png';
  image.alt = 'Google';
  image.className = 'h-6 w-auto';

  element.appendChild(image);

  return new Control({ element });
};

// Create custom zoom slider control
export const createZoomSliderControl = () => {
  const zoomLabelElement = document.createElement('div');

  zoomLabelElement.className = 'ol-zoom-level ol-unselectable ol-control';

  Object.assign(zoomLabelElement.style, {
    backgroundColor: 'rgba(255,255,255,0.8)',
    borderRadius: '4px',
    border: '1px solid #ccc',
    padding: '2px 5px',
    color: '#333',
    fontSize: '11px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    // Position it explicitly if you aren't using CSS classes
    position: 'absolute',
    top: '10px',
    right: '50px', // Adjust based on where you want it
  });

  const zoomLabelControl = new Control({ element: zoomLabelElement });
  const zoomSliderControl = new ZoomSlider();

  return { zoomLabelControl, zoomSliderControl };
};

export interface OLFeatureResult {
  feature: Feature;
  layerDef: LayerDefinition;
}

// Find an OL feature by ID across all vector layers including nested groups
export const findOLFeatureById = (
  map: Map,
  featureId: string,
): OLFeatureResult | null => {
  let found: OLFeatureResult | null = null;

  const search = (collection: any) => {
    collection.forEach((l: any) => {
      if (found) return;
      if (l instanceof LayerGroup) {
        search(l.getLayers());
      } else if (l instanceof VectorLayer) {
        const feature = l.getSource()?.getFeatureById(featureId);

        if (feature) {
          found = { feature, layerDef: l.get('config') };
        }
      }
    });
  };

  search(map.getLayers());
  return found;
};

// Find OL layer by layer definition ID
export const findOLLayerByDef = (map: Map, layerId: string): any | null => {
  let found: any = null;

  const search = (collection: any) => {
    collection.forEach((l: any) => {
      if (found) return;
      if (l instanceof LayerGroup) {
        search(l.getLayers());
      } else if (l.get('config')?.id === layerId) {
        found = l;
      }
    });
  };

  search(map.getLayers());
  return found;
};

// Get all vector layers flattened — useful for iterating
export const getAllVectorLayers = (map: Map): VectorLayer<any>[] => {
  const result: VectorLayer<any>[] = [];

  const search = (collection: any) => {
    collection.forEach((l: any) => {
      if (l instanceof LayerGroup) {
        search(l.getLayers());
      } else if (l instanceof VectorLayer) {
        result.push(l);
      }
    });
  };

  search(map.getLayers());
  return result;
};

export const findLayerForAsset = (
  layers: LayerDefinition[],
  assetType: string,
): LayerDefinition | null =>
  layers.find(
    (l) =>
      l.sourceTable === 'assets' &&
      (l.sourceFilter as any)?.assetType === assetType,
  ) ?? null;
